import { useCallback, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { BaseLogo } from "../../../../core/models/entities/BaseLogo";
import { enviroment } from "../../../../enviroment";
import { useProductPendingPoll } from "../../../../hooks/useProductPendingPoll";
import { logoDetailKey } from "../../../../data/repositories/logoDetailCache";
import {
  isUploadKindPending,
  isUploadRejected,
} from "../../../../utils/functions/productPending";
import {
  initialOnDemandReadState,
  nextOnDemandRead,
  OnDemandReadState,
  UPLOAD_KIND_POLL_MS,
} from "../../../../utils/functions/uploadKindPoll";

// Flag REACT_APP_UPLOAD_PENDING_ENABLED - build-time constant, so the export
// at the bottom is fixed for the lifetime of the bundle and hook order never
// varies at runtime (same idiom as useEditorUploadPendingPoll).
const uploadPendingEnabled = enviroment.uploadPendingEnabled;

export interface EditorUploadKindPoll {
  /**
   * The user touched a locked control (colour, method, size, Save &
   * approve): ONE extra GetLogo, throttled to 1 per 10 s and at most 2 per
   * logo per editor session; a no-op while nothing is pending.
   */
  touchLocked: () => void;
  /**
   * ONE GetLogo right now, outside the on-demand cap, for the moment the
   * user SAVES while the upload is still pending (Save draft): the PUT must
   * carry the row's CURRENT method - the worker may have landed and chosen
   * the Digital method meanwhile (a poll not yet delivered, or the 4 + 2
   * budget spent during a long outage), and the API refuses the draft's
   * locked default on a digital row. Resolves with the fresh detail entry
   * (undefined while nothing is pending or when the read could not run);
   * the heal subscription delivers the same entry to the canvas sync, so
   * the save also patches the landing in. Never rejects.
   */
  readNow: () => Promise<BaseLogo | undefined>;
}

type EditorUploadKindPollHook = (
  logo: BaseLogo | null | undefined
) => EditorUploadKindPoll;

/**
 * Editor-level re-read for an UPLOAD the API's worker still owes its
 * supplier identity (flag REACT_APP_UPLOAD_PENDING_ENABLED, design 14/9
 * §5.4).
 *
 * While the open logo is upload-kind pending and not rejected, re-read ONLY
 * its own GetLogo entry (["logos", actAs, id]) on the 2/2/3/3 s plan - max 4
 * calls, never a list call. The heal subscription (useEditorLogoHeal, flag
 * REACT_APP_LOGO_HEAL_PER_MUTATION) delivers the result and
 * patchLogoFromDetail syncs serial / mm / colours / the worker's method /
 * the high-res, so the badge clears and the locks open without F5. The loop
 * stops the moment uploadPending clears or uploadFailedReason is set (the
 * rejection dialog takes over), and the budget restarts per logo id.
 *
 * On top of the plan, `touchLocked` fires ONE extra GetLogo when the user
 * touches a locked control - a user waiting through a long supplier outage
 * gets an answer the moment they act, but never more than 4 + 2 calls from
 * one editor. The 3/8/15 s clone re-read, the sharp-wait re-read and the
 * heal all stand down for an upload-kind logo (see their call sites), so
 * this plan IS the budget.
 */
function useEditorUploadKindPollEnabled(
  logo: BaseLogo | null | undefined
): EditorUploadKindPoll {
  const queryClient = useQueryClient();
  const logoId = logo?.id ?? 0;
  const pending =
    logoId > 0 && isUploadKindPending(logo) && !isUploadRejected(logo);
  useProductPendingPoll(
    pending ? [logoDetailKey(logoId)] : undefined,
    pending,
    UPLOAD_KIND_POLL_MS
  );

  // Read at call time (the touch handler is held by the tool across renders).
  const latest = useRef({ logoId, pending });
  latest.current = { logoId, pending };
  const onDemand = useRef<OnDemandReadState>(initialOnDemandReadState());

  const touchLocked = useCallback(() => {
    const { logoId: id, pending: isPending } = latest.current;
    if (!isPending) return;
    const next = nextOnDemandRead(onDemand.current, id, Date.now());
    if (!next) return;
    onDemand.current = next;
    void queryClient.invalidateQueries({
      queryKey: logoDetailKey(id),
      exact: true,
      refetchType: "all",
    });
  }, [queryClient]);

  const readNow = useCallback(async () => {
    const { logoId: id, pending: isPending } = latest.current;
    if (!isPending) return undefined;
    const key = logoDetailKey(id);
    // refetchQueries never rejects without throwOnError; an entry without a
    // query function (heal flag off = no observer) is simply not refetched
    // and the cached value is returned as it is.
    await queryClient.refetchQueries({ queryKey: key, exact: true });
    return queryClient.getQueryData<BaseLogo>(key);
  }, [queryClient]);

  return { touchLocked, readNow };
}

// Flag off: no hook body at all (no query-client read, no timer) and a
// shared no-op handler - the call site keeps one hook call either way.
const NOOP_POLL: EditorUploadKindPoll = {
  touchLocked: () => undefined,
  readNow: () => Promise.resolve(undefined),
};
function useEditorUploadKindPollDisabled(): EditorUploadKindPoll {
  return NOOP_POLL;
}

export const useEditorUploadKindPoll: EditorUploadKindPollHook =
  uploadPendingEnabled
    ? useEditorUploadKindPollEnabled
    : useEditorUploadKindPollDisabled;
